import Axios from 'axios';
import { action, observable } from 'mobx';
import { OrderParams, PageInfo, ScrollPageInfo } from '@/models/data';
import { Notification } from 'element-react';
import { API } from '@/config/api';
import i18n from '@/i18n';
import { getBaseUrlBySymbol, getBaseUrlByUnderlyingType } from '@/utils/futures';
import UnderLyingEnum from '@/@enums/UnderLyingEnum';

export interface OrderItem {
  avgPrice: string; //	成交均价
  closePosition: boolean; //	是否条件全平仓
  createdTime: number; //	创建时间
  sourceId: string; //	条件触发id
  executedQty: string; //	已成交数量
  marginFrozen: string; //	占用保证金
  orderId: string; //	订单id
  orderSide: OrderSide; //	买卖方向
  orderType: string; //	订单类型
  origQty: string; // 	数量
  positionSide: PositionSide; // 	持仓方向
  price: string; //	委托价格
  state: OrderState; //	订单状态 1：新建订单（未成交）；2：部分成交；3：全部成交；4：已撤销；5：平台撤销（拒绝）；6：已过期
  symbol: string; //	交易对
  timeInForce: string; // 有效类型
  closeProfit: string; // 平仓盈亏
  forceClose?: boolean; // 是否爆仓
  // 其他配置
  contractSize: string;
}

export interface OrderBillItem {
  fee: number;
  feeCoin: string;
  orderId: number;
  price: number;
  quantity: number;
  symbol: string;
  timestamp: number;
}

export default class Order {
  @observable orderList: OrderItem[];

  @observable orderListPageInfo: PageInfo;

  @observable orderRecord: OrderItem[];

  @observable orderRecordPageInfo: ScrollPageInfo;

  @observable withdrawOrderList: string[];

  @observable orderBillList: OrderBillItem[];

  @observable orderBillListPageInfo: PageInfo;

  @observable isFetchingOrderList: boolean;

  @observable isFetchingOrderRecord: boolean;

  @observable isFetchingOrderBillList: boolean;

  @observable isFetchingLiquidationRecord: boolean;

  @observable liquidationRecord: OrderItem[];

  @observable liquidationRecordPageInfo: ScrollPageInfo;

  constructor() {
    this.orderList = [];
    this.orderListPageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };

    this.orderRecord = [];
    this.orderRecordPageInfo = {
      hasPrev: false,
      hasNext: false,
    };

    this.withdrawOrderList = [];

    this.orderBillList = [];
    this.orderBillListPageInfo = {
      page: 1,
      size: 10,
      total: 0,
    };

    this.liquidationRecord = [];
    this.liquidationRecordPageInfo = {
      hasPrev: false,
      hasNext: false,
    };

    this.isFetchingOrderList = false;
    this.isFetchingOrderRecord = false;
    this.isFetchingOrderBillList = false;
    this.isFetchingLiquidationRecord = false;
  }

  @action.bound
  setLoadingState(key: string, data: boolean) {
    // @ts-ignore
    this[key] = data;
  }

  @action
  setOrderList(data: OrderItem[]) {
    this.orderList = data;
  }

  @action.bound
  setOrderListPageInfo(data: PageInfo) {
    const origin = { ...this.orderListPageInfo };
    this.orderListPageInfo = { ...origin, ...data };
  }

  @action
  setOrderRecord(data: OrderItem[]) {
    this.orderRecord = data;
  }

  @action.bound
  setOrderRecordPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.orderRecordPageInfo };
    this.orderRecordPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchOrderList(params?: {
    symbol?: string;
    page?: number;
    size?: number;
    state?: string;
    underlyingType?: UnderLyingEnum;
  }) {
    this.setLoadingState('isFetchingOrderList', true);
    const orderListPageInfo = this.orderListPageInfo;

    const config: any = {
      params: {
        page: orderListPageInfo.page,
        size: orderListPageInfo.size,
        ...params,
      },
    };

    if (params?.symbol) {
      config.baseURL = getBaseUrlBySymbol(params.symbol);
    } else if (params?.underlyingType) {
      config.baseURL = getBaseUrlByUnderlyingType(params?.underlyingType);
    }

    Reflect.deleteProperty(config.params, 'underlyingType');

    const result: any = await Axios.get(API.ORDER_LIST, config);

    if (result) {
      const { items, page, ps: size, total } = result;

      if (params?.state !== 'HISTORY') {
        if (Array.isArray(items)) {
          this.setOrderList(items);

          this.setOrderListPageInfo({ page, size, total });
        }
      }
    }

    this.setLoadingState('isFetchingOrderList', false);
  }

  @action.bound
  async fetchOrderHistory({
    symbol,
    direction,
    limit = 10,
    id,
    startTime,
    endTime,
    underlyingType,
  }: {
    symbol?: string;
    direction?: ScrollPaginationDirection;
    limit?: number;
    id?: string;
    startTime?: number | string;
    endTime?: number | string;
    underlyingType?: UnderLyingEnum;
  }) {
    this.setLoadingState('isFetchingOrderRecord', true);

    const config: any = {
      params: {
        symbol,
        direction,
        limit,
        id,
        startTime,
        endTime,
      },
    };

    if (symbol) {
      config.baseURL = getBaseUrlBySymbol(symbol);
    } else if (underlyingType) {
      config.baseURL = getBaseUrlByUnderlyingType(underlyingType);
    }

    const result: any = await Axios.get(API.ORDER_HISTORY, config);

    if (result) {
      const { hasPrev, hasNext, items } = result;

      this.setOrderRecord(items || []);
      this.setOrderRecordPageInfo({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingOrderRecord', false);
  }

  // @action.bound
  // updateOrderListByRow = debounce((params: OrderItem) => {
  //   const { orderId } = params;
  //   const index = this.orderList.findIndex(item => item.orderId === orderId);
  //
  //   const newList = [...this.orderList];
  //   if (index > -1) {
  //     const target = newList[index];
  //     const newItem = {
  //       ...target,
  //       ...params,
  //     };
  //
  //     if (ORDER_UNCOMPLETED_STATE_LIST.includes(params.state)) {
  //       newList.splice(index, 1, newItem);
  //     } else {
  //       newList.splice(index, 1);
  //     }
  //   } else if (ORDER_UNCOMPLETED_STATE_LIST.includes(params.state)) {
  //     newList.unshift(params);
  //   }
  //
  //   this.setOrderList(newList);
  // }, 300);

  @action.bound
  async fetchWithdrawOrder(id: string) {
    this.setWithdrawOrderList([...this.withdrawOrderList, id]);
    const result: any = await Axios.post(API.ORDER_CANCEL, { orderId: id }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.withdraw'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      const newOrderList = [...this.orderList];
      const index = newOrderList.findIndex((item: OrderItem) => id === item.orderId);
      newOrderList.splice(index, 1);
      this.setOrderList(newOrderList);

      const { page, size, total } = this.orderListPageInfo;
      this.setOrderListPageInfo({ page, size, total: total - 1 });

      Notification.success({
        message: i18n.t('success.withdraw'),
        offset: 60,
        duration: 2000,
      });

      this.setWithdrawOrderList([...this.withdrawOrderList.filter(item => item !== id)]);
    }
  }

  // 撤销所有订单 当前委托
  @action.bound
  async fetchWithdrawOrderAll(symbol?: string) {
    const result: any = await Axios.post(API.ORDER_CANCEL_ALL, { symbol }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || i18n.t('failure.withdraw'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      const data = {
        page: 1,
        state: 'UNFINISHED',
        symbol,
      };
      this.fetchOrderList(data);

      Notification.success({
        message: i18n.t('success.withdraw'),
        offset: 60,
        duration: 2000,
      });
      return Promise.resolve(result);
    }
  }

  // 更新现有订单列表
  // 如果是本地更新，当前页不是第一页时没有必要更新；
  // @action.bound
  // addOrderToList(newItem: OrderItem, isLocal = false) {
  //   const { page, size, total } = this.orderListPageInfo;
  //   if (isLocal) {
  //     // 如果不是第一页，没有必要将本地产生的订单加入列表；
  //     if (page !== 1) return;
  //   }
  //
  //   const newList = [newItem, ...this.orderList];
  //   if (newList.length > size) {
  //     newList.pop();
  //   }
  //
  //   this.setOrderList(newList);
  //   this.setOrderListPageInfo({ page, size, total: total + 1 });
  // }

  @action.bound
  setWithdrawOrderList(data: string[]) {
    this.withdrawOrderList = data;
  }

  @action.bound
  isWithdrawingOrder(id: string) {
    return this.withdrawOrderList.includes(id);
  }

  @action.bound
  setOrderBillList(data: OrderBillItem[]) {
    this.orderBillList = data;
  }

  @action.bound
  setOrderBillListPageInfo(data: PageInfo) {
    const origin = { ...this.orderBillListPageInfo };
    this.orderBillListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchOrderBillList(params: { orderId: string; page?: number; size?: number }) {
    this.setLoadingState('isFetchingOrderBillList', true);

    const result: any = await Axios.get(API.ORDER_BILL_LIST, {
      params,
    });

    if (result) {
      const { items, page, ps: size, total } = result;

      if (Array.isArray(items)) {
        this.setOrderBillList(items);

        this.setOrderBillListPageInfo({ page, size, total });
      }
    }

    this.setLoadingState('isFetchingOrderBillList', false);
  }

  @action.bound
  async fetchCreateOrder(params: OrderParams) {
    const result: any = await Axios.post(API.ORDER_CREATE, params).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || error || i18n.t('failure.order'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      Notification.success({
        message: i18n.t('success.order'),
        offset: 60,
        duration: 2000,
      });
    }
  }

  //
  // 强平记录
  //

  @action
  setLiquidationRecord(data: OrderItem[]) {
    this.liquidationRecord = data;
  }

  @action.bound
  setLiquidationRecordPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.liquidationRecordPageInfo };
    this.liquidationRecordPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchLiquidationRecord({
    symbol,
    direction,
    limit = 20,
    id,
    startTime,
    endTime,
  }: {
    symbol?: string;
    direction: ScrollPaginationDirection;
    limit?: number;
    id?: string;
    startTime?: number;
    endTime?: number;
  }) {
    this.setLoadingState('isFetchingLiquidationRecord', true);

    const result: any = await Axios.get(API.ORDER_HISTORY, {
      params: {
        symbol,
        direction,
        limit,
        id,
        startTime,
        endTime,
        forceClose: true,
      },
    });

    if (result) {
      const { hasPrev, hasNext, items } = result;

      this.setLiquidationRecord(items || []);
      this.setLiquidationRecordPageInfo({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingLiquidationRecord', false);
  }
}
